<script>
export const pageName = 'rockets';
export const pageRoute = '/rockets';
export const title = 'Rockets'
export const requireMembership = true;

import RocketView from '../../../functions/domain/rockets/rocketView.js';
import { listenForProjections } from '@trullock/firebase-client';
import UserView from '../../../functions/domain/users/userView.js';
import { getCurrentUserId } from '../../js/auth.js'
import { hideLoading, showLoading } from '../../js/utils/loading.js';
import { commands } from '../../js/lib/functions.js';
import { showToast } from '../../js/utils/toast.js'
import { showConfirm } from '../../js/utils/confirm.js';
import MapFlightCardUrl from '../../resources/MAP-and-Record-flight-card-A4-v3-1.pdf'
import CertFlightCardUrl from '../../resources/Cert-and-Record-flight-card-A4-v3-1.pdf'

export default {
	data() {
		return { 
			rockets: [],
			sort: 'a-z',
			MapFlightCardUrl,
			CertFlightCardUrl,
			ready: false
		}
	},

	methods: {
		async boot(opts) {
			await listenForProjections([UserView, getCurrentUserId(), RocketView], this.rockets, () => {
				this.doSort()
			});
			this.ready = true;
		},		
		deleteRocket(rocket) {
			commands.warmUp();

			showConfirm({
				title: 'Confirm deletion',
				message: `Are you sure you want to delete this rocket and all its associated flights?`,
				buttons: [
					{
						text: 'Cancel',
						style: 'white'
					},
					{
						text: 'Delete rocket',
						style: 'danger',
						action: async () => {

							showLoading();
							
							let result = await commands.rockets.delete({ rocketId: rocket.id })
							
							hideLoading();

							if(!result.success)
							{
								showToast({ message: 'Error deleting rocket', style: 'bg-danger'})
								return;
							}

							showToast({
								message: 'Rocket deleted',
								style: 'bg-success'
							})
						}
					}
				]
			})
		},
		doSort()
		{
			switch(this.sort)
			{
				case 'a-z':
					// rockets dont have a name on creation, so its possible a fast listener can receive a nameless rocket, so we need to handle that
					this.rockets.sort((a, b) => a.name?.localeCompare(b.name) || 0);
					break;
				case 'latest-flight':
					this.rockets.sort((a, b) => (b.latestFlight?.flownOn || 0) - (a.latestFlight?.flownOn || 0));
					break;
				case 'created':
					this.rockets.sort((a, b) => b.createdOn - a.createdOn);
					break;
			}
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<div class="row justify-content-center">
			<div class="col-12 col-md-8 col-xl-6 mb-5">
				<h1 class="display-4 text-center mb-3">Rocket flight logs</h1>
				<p class="text-muted text-center mb-5">Create rockets and log flights against them.</p>
				<p class="alert alert-light mb-4"><span class="fe fe-info"></span> Looking for where to submit Certification and MAP flights? Add your rocket and then use the subsequent Add Flight page.</p>
				<p class="text-muted text-center mb-2"><a :href="CertFlightCardUrl" target="_blank">Download the Certification/Record Attempt Flight Card <span class="fe fe-download"></span></a> for use in the field.</p>
				<p class="text-muted text-center mb-5"><a :href="MapFlightCardUrl" target="_blank">Download the MAP Flight Card <span class="fe fe-download"></span></a> for use in the field.</p>

				<a href="/rockets/add" class="btn btn-block btn-primary">Add new rocket <span class="fe fe-plus-circle"></span></a>

				<hr>
				<form v-if="rockets.length >= 2" action="">
					<div class="form-group row align-items-center">
						<div class="col-auto">
							<label>Sort:</label>
						</div>
						<div class="col">
							<select v-model="sort" class="form-control" @change="doSort">
								<option value="a-z">A-Z</option>
								<option value="latest-flight">Latest Flight</option>
								<option value="created">Date Created</option>
							</select>
						</div>
					</div>
				</form>

				<template v-if="rockets.length">
					<div class="card" v-for="rocket in rockets">
						<span v-if="rocket.hasMAPFlights || rocket.hasCertFlights || rocket.hasRecordFlights" class="badge badge-primary" style="position: absolute; top:-5px; right: -5px;">
							<span class="fe fe-award"></span>
							<span>MAP/Cert/Record Flights</span>
						</span>
						<div class="card-body">
							<span class="small"></span>
							<div class="row align-items-center">
								<div v-if="rocket.photoUrl" class="col-auto">
									<img :src="rocket.photoUrl" :alt="`Photo of ${rocket.name}`" class="avatar-lg" />
								</div>
								<div class="col ms-n2">
									<h4 class="mb-1">
										<a :href="`/rockets/${rocket.id}`" class="">{{ rocket.name }}</a>
									</h4>
									<p class="small text-muted mb-0">
										Flights: {{ rocket.flights.length }}
									</p>
									<p class="small mt-2 mb-0" v-if="rocket.notes">{{ rocket.notes }}</p>
								</div>
								<div class="col-auto">
									<a :href="`/rockets/${rocket.id}`" class="btn btn-sm btn-outline-primary">
										View
										<span class="fe fe-arrow-right-circle"></span>
									</a>
								</div>
								<div class="col-auto px-0">
									<div class="dropdown">
										<button class="btn p-0 dropdown-ellipses dropdown-toggle" aria-haspopup="true" aria-expanded="true">
											<i class="fe fe-more-vertical"></i>
										</button>
										<div class="dropdown-menu dropdown-menu-end">
											<a :href="`/rockets/${rocket.id}/edit`" class="dropdown-item text-primary">
												<span class="fe fe-edit"></span> Edit
											</a>
											<a :href="`/rockets/${rocket.id}/flights/add`" class="dropdown-item text-primary">
												<span class="fe fe-send"></span> Add flight
											</a>
											<button :disabled="rocket.locked" @click.prevent="deleteRocket(rocket)" class="dropdown-item dropdown-item-danger">
												<span class="fe fe-trash-2"></span> Delete
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</template>
				<div class="text-center" v-else>
					<div class="card">
						<div class="card-body">
							<p class="text-muted mb-0">You haven't added any rockets yet.</p>
						</div>
					</div>
				</div>
			</div>
		</div>
    </div>
</template>
